import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import * as authService from '../../services/authService'; // For login and register
import { setAuthHeader } from '../../services/apiClient'; // Import setAuthHeader from centralized apiClient

// Interfaces for API data
export interface User {
  id: string;
  username: string;
  email: string;
  roles: string[];
  features: string[]; // Added to store enabled features for the user
}

interface AuthResponseData {
  user: User;
  accessToken: string;
}

// Define a type for the slice state
interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  features: string[]; // Add features to the auth state
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null | Record<string, any>; // Allow more detailed error object
}

// Attempt to load user and token from localStorage
const storedUserJSON = localStorage.getItem('authUser');
const storedToken = localStorage.getItem('authToken');
const storedUser = storedUserJSON ? JSON.parse(storedUserJSON) : null;

const initialState: AuthState = {
  isAuthenticated: !!storedToken,
  user: storedUser,
  token: storedToken,
  features: storedUser?.features || [], // Correctly initialize features
  status: 'idle',
  error: null,
};

if (initialState.isAuthenticated) {
  setAuthHeader(storedToken); // Set the auth header if token exists
}

// Updated RegisterData no longer includes email field
// Async thunk for login
export const loginUser = createAsyncThunk<
  AuthResponseData, // Return type of the payload creator
  authService.LoginCredentials, // First argument to the payload creator
  { rejectValue: any } // Types for ThunkAPI
>('auth/loginUser', async (credentials, thunkAPI) => {
  try {
    const data = await authService.login(credentials);
    localStorage.setItem('authToken', data.accessToken);
    localStorage.setItem('authUser', JSON.stringify(data.user));
    setAuthHeader(data.accessToken); // Update axios header
    return data;
  } catch (error: any) {
    setAuthHeader(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    return thunkAPI.rejectWithValue(error.response?.data || { message: 'Login failed' });
  }
});

// Async thunk for registration
export const registerUser = createAsyncThunk<
  User, // Return type (assuming backend returns user object on success)
  authService.RegisterData,
  { rejectValue: any }
>('auth/registerUser', async (userData, thunkAPI) => {
  try {
    const data = await authService.register(userData);
    // Typically, after registration, you might want to redirect to login
    // or automatically log the user in. For now, just return user data.
    return data.user; // Assuming register returns { user: User }
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data || { message: 'Registration failed' });
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // setAuth removed as loginUser.fulfilled handles this
    // setAuthLoading removed as thunk pending state handles this
    // setAuthFailed removed as thunk rejected state handles this
    logout(state) {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.features = [];
      state.status = 'idle';
      state.error = null;
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      setAuthHeader(null); // Clear axios header
    },
    clearAuthError(state) {
      state.error = null;
      state.status = 'idle';
    },
  },
  extraReducers: builder => {
    builder
      // Login User
      .addCase(loginUser.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action: PayloadAction<AuthResponseData>) => {
        state.status = 'succeeded';
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.accessToken;
        state.features = action.payload.user.features;
        state.error = null;

        // Persist user and features to localStorage
        localStorage.setItem('authUser', JSON.stringify(action.payload.user));
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = 'failed';
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.features = [];
        state.error = action.payload || { message: 'Login failed due to an unknown error.' };
      })
      // Register User
      .addCase(registerUser.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(registerUser.fulfilled, state => {
        // action.payload is User, but we don't auto-login here
        state.status = 'succeeded'; // Indicates registration success, not necessarily login
        state.error = null;
        // User will be redirected to login page by the component
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || { message: 'Registration failed due to an unknown error.' };
      });
  },
});

export const { logout, clearAuthError } = authSlice.actions;

export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectUser = (state: RootState) => state.auth.user;
export const selectAuthToken = (state: RootState) => state.auth.token;
export const selectAuthStatus = (state: RootState) => state.auth.status;
export const selectAuthError = (state: RootState) => state.auth.error;
export const selectUserFeatures = (state: RootState) => state.auth.features;
export const selectUserRoles = (state: RootState) => state.auth.user?.roles ?? [];

export default authSlice.reducer;
